export type GrantOpportunityStatus =
  | "interested"
  | "preparing"
  | "submitted"
  | "awarded"
  | "declined"
  | "closed";

export type GrantOpportunitySource = "manual" | "catalog" | "regional";

export type GrantOpportunityReviewer = {
  id: number;
  user_id: number;
  email: string;
};

export type GrantOpportunityContributor = {
  id: number;
  user_id: number;
  email: string;
};

// An opportunity is scoped either to the team as a whole or to one department.
// Derived server-side from department_id, so the two can never disagree.
export type GrantOpportunityScopeType = "team" | "department";

// Whether the team is applying for the grant itself or supporting someone
// else's application. Only an AI Partner opportunity has one (ENG-1824).
export type GrantOpportunityEngagementType = "applying" | "supporting";

export type GrantOpportunity = {
  id: number;
  municipality_id: number;
  user_id: number;
  department_id: number | null;
  scope_type: GrantOpportunityScopeType;
  grant_id: number | null;
  title: string;
  url: string | null;
  notes: string | null;
  watching: boolean;
  deadline_at: string | null;
  assigned_to_id: number | null;
  assigned_to_name: string | null;
  document_folder_id?: number | null;
  reviewers: GrantOpportunityReviewer[];
  contributors: GrantOpportunityContributor[];
  requested_amount: number | null;
  awarded_amount: number | null;
  award_date: string | null;
  funder_portal_name: string | null;
  funder_portal_url: string | null;
  funder_portal_notes: string | null;
  // AI Partner mode: takes an uploaded package and an AI-drafted plan.
  ai_partner: boolean;
  engagement_type: GrantOpportunityEngagementType | null;
  outside_beneficiary_name: string | null;
  status: GrantOpportunityStatus;
  source: GrantOpportunitySource;
  created_at: string;
  updated_at: string;
};

export type GrantOpportunityPayload = {
  title?: string;
  url?: string;
  notes?: string;
  watching?: boolean;
  deadline_at?: string | null;
  status?: GrantOpportunityStatus;
  grant_id?: number;
  assigned_to_id?: number | null;
  requested_amount?: number | null;
  awarded_amount?: number | null;
  award_date?: string | null;
  // Omit for a team-scoped opportunity; set to place it in one department.
  department_id?: number | null;
  // Reference-only funder portal record; the product never calls the portal.
  funder_portal_name?: string | null;
  funder_portal_url?: string | null;
  funder_portal_notes?: string | null;
  // Opts the opportunity into package upload and AI planning. Rejected with
  // 403 unless the team carries the ai_partner flag.
  ai_partner?: boolean;
  engagement_type?: GrantOpportunityEngagementType;
  outside_beneficiary_name?: string | null;
};

const jsonHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

export function exportGrantOpportunitiesCsvUrl(municipalityId: number): string {
  return `/api/v1/municipalities/${municipalityId}/grant_opportunities/export`;
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const plainMatch = header.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] ?? null;
}

export async function downloadGrantOpportunitiesCsv(municipalityId: number): Promise<void> {
  const response = await fetch(exportGrantOpportunitiesCsvUrl(municipalityId), {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Failed to export grant pipeline");
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download =
    filenameFromContentDisposition(response.headers.get("Content-Disposition")) ??
    `grant-pipeline-${municipalityId}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// Omitting departmentId returns the team Tracker list, which excludes
// department-scoped opportunities. Pass a departmentId to read one department
// instead. No call returns both.
export async function listGrantOpportunities(
  municipalityId: number,
  options?: { watching?: boolean; departmentId?: number },
): Promise<GrantOpportunity[]> {
  const params = new URLSearchParams();
  if (options?.watching !== undefined) {
    params.set("watching", String(options.watching));
  }
  if (options?.departmentId !== undefined) {
    params.set("department_id", String(options.departmentId));
  }
  const query = params.toString();
  const url = `/api/v1/municipalities/${municipalityId}/grant_opportunities${query ? `?${query}` : ""}`;
  const response = await fetch(url, { credentials: "include", headers: { Accept: "application/json" } });
  if (!response.ok) {
    throw new Error("Failed to load grant opportunities");
  }
  return response.json();
}

export async function saveGrantToWatchlist(
  municipalityId: number,
  grantId: number,
): Promise<GrantOpportunity> {
  return saveGrantFromCatalog(municipalityId, grantId, { watching: true });
}

export async function saveGrantFromCatalog(
  municipalityId: number,
  grantId: number,
  options: { watching: boolean },
): Promise<GrantOpportunity> {
  const response = await fetch(
    `/api/v1/municipalities/${municipalityId}/grant_opportunities/from_grant`,
    {
      method: "POST",
      credentials: "include",
      headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({
        grant_id: grantId,
        watching: options.watching,
      }),
    },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.errors?.join(", ") ?? "Failed to save grant to tracking");
  }
  return response.json();
}

export async function createGrantOpportunity(
  municipalityId: number,
  payload: GrantOpportunityPayload,
): Promise<GrantOpportunity> {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/grant_opportunities`, {
    method: "POST",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ grant_opportunity: payload }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.errors?.join(", ") ?? "Failed to create grant opportunity");
  }
  return response.json();
}

export type BulkUpdateGrantOpportunitiesResult = {
  updated_count: number;
  success_ids: number[];
  failed: Array<{ id: number; errors: string[] }>;
};

export async function bulkUpdateGrantOpportunities(
  municipalityId: number,
  ids: number[],
  payload: Partial<GrantOpportunityPayload>,
): Promise<BulkUpdateGrantOpportunitiesResult> {
  const response = await fetch(
    `/api/v1/municipalities/${municipalityId}/grant_opportunities/bulk_update`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({ ids, grant_opportunity: payload }),
    },
  );
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.errors?.join(", ") ?? "Failed to bulk update grant opportunities");
  }
  return body as BulkUpdateGrantOpportunitiesResult;
}

export async function updateGrantOpportunity(
  municipalityId: number,
  id: number,
  payload: Partial<GrantOpportunityPayload>,
): Promise<GrantOpportunity> {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/grant_opportunities/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ grant_opportunity: payload }),
  });
  if (!response.ok) {
    throw new Error("Failed to update grant opportunity");
  }
  return response.json();
}

export async function deleteGrantOpportunity(municipalityId: number, id: number): Promise<void> {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/grant_opportunities/${id}`, {
    method: "DELETE",
    credentials: "include",
    headers: { "X-CSRF-Token": csrfToken() },
  });
  if (!response.ok) {
    throw new Error("Failed to delete grant opportunity");
  }
}

export const GRANT_OPPORTUNITY_STATUS_LABELS: Record<GrantOpportunityStatus, string> = {
  interested: "Interested",
  preparing: "Preparing",
  submitted: "Submitted",
  awarded: "Awarded",
  declined: "Declined",
  closed: "Closed",
};
